/* eslint-disable react/prop-types */
// Orchestrator for the calendar sidebar "phone" preview panel.
// When viewing a single athlete's calendar, shows that athlete's day of workouts
// (view-only), with a date header to move day-by-day and a tap-through to a
// view-only exercise detail. Renders blank when no single athlete is selected.
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import DatePicker from 'react-datepicker';
import { VariableSizeList as List } from 'react-window';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import 'react-datepicker/dist/react-datepicker.css';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import Button from '../../../../shared/components/Button/Button';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import { showSuccessToast, showErrorToast } from '../../../../shared/utils/toastManager';
import {
  assignProgramToAthlete,
  getPreviewWorkoutItems,
  getPreviewWorkoutItemsFreeForm,
  setPreviewActiveExerciseIndex,
  setPreviewDate,
  setPreviewDayNumber,
} from '../../ducks/calendarActions';
import flattenPreviewWorkouts from '../../utils/flattenPreviewWorkouts';
import CalendarWorkoutPreviewList from './CalendarWorkoutPreviewList';
import CalendarExerciseDetail from './CalendarExerciseDetail';
import CalendarExerciseNav from './CalendarExerciseNav';
import SideBarTitleWrapper from './SideBarTitleWrapper';

// Based on the Tag Filter dropdown in LiftExercisesContainer; the option and menu
// padding follow the flat Settings menus.
const makeCustomSelectStyles = (colors) => ({
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '16px',
    height: '44px',
    fontWeight: 400,
    background: colors.surfaceInput,
    border: `2px solid ${colors.borderLight}`,
    boxShadow: state.isFocused ? 'none' : 'none',
    '&:hover': {
      borderColor: colors.borderLight,
    },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: '8px 12px',
    backgroundColor: state.isSelected ? colors.surfaceSecondary : colors.surfaceElevated,
    color: state.isSelected ? colors.textTertiary : colors.text,
    fontFamily: 'Nunito Sans',
    fontSize: '16px',
    // eslint-disable-next-line no-underscore-dangle
    fontWeight: state.data.__isNew__ ? 700 : 400,
    '&:hover': {
      backgroundColor: colors.surfaceSecondary,
    },
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: colors.surfaceSecondary,
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '4px',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: colors.text,
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: colors.textSecondary,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: colors.surfaceElevated,
    color: colors.text,
    // The default react-select menu shadow gets clipped by the sidebar's overflow:hidden, so
    // use an explicit border (inside the box, not clipped) to match the Tag Filter's ring.
    border: `1px solid ${colors.borderLight}`,
    borderRadius: '8px',
    minWidth: '250px',
    padding: '4px 0',
    marginTop: '0px',
    marginBottom: '0px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: colors.text,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: colors.textSecondary,
  }),
  input: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? 'inherit' : colors.text,
  }),
});

// Windowed menu list for the athlete SelectBoxes so the full-roster option list doesn't
// render every row at once (react-select's default). Mirrors the MemoizedMenuList pattern
// used across the calendar module (e.g. CalendarCreateContent, SaveWorkoutModal).
const MenuList = (props) => {
  const { children, maxHeight } = props;
  const listRef = useRef({});
  const rowHeights = useRef({});
  // No matches: children is react-select's single
  // NoOptionsMessage node (no .length) — render as-is.
  if (!children?.length) {
    return <div>{children}</div>;
  }
  const height = Math.min(children.length * 40, maxHeight);
  const getRowHeight = (index) => rowHeights.current[index] + 8 || 40;

  return (
    <div style={{ height }}>
      <List
        ref={listRef}
        height={height}
        itemCount={children.length}
        itemSize={(index) => getRowHeight(index)}
      >
        {({ index, style }) => {
          const rowRef = useRef({});

          useEffect(() => {
            if (rowRef.current) {
              const rowHeight = rowRef.current.getBoundingClientRect().height;
              /** Store this index's height and reset the List after each update */
              if (rowHeights.current[index] !== rowHeight) {
                rowHeights.current[index] = rowHeight;
                listRef.current?.resetAfterIndex(index); /** recalc sizes from this index */
              }
            }
          }, [index]);

          return (
            <div style={style}>
              <div ref={rowRef}>
                {children[index]}
              </div>
            </div>
          );
        }}
      </List>
    </div>
  );
};

const MemoizedMenuList = React.memo(MenuList);

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

// Zooms ONLY the exercise detail card at the 384px sidebar width (>=1024px window), like the
// workouts carousel scaling the card you're viewing. Counter-sizing the box (width/height at
// 1/1.15) so the scaled card fills the same space keeps its internal padding/spacing proportional
// and its scroll container intact. Pass-through at <=1023px, so 300px/mobile are unchanged.
const ZoomWrapper = styled('div')`
  width: 100%;
  height: 100%;

  @media screen and (min-width: 1024px) {
    transform: scale(1.15);
    transform-origin: top left;
    width: calc(100% / 1.15);
    height: calc(100% / 1.15);
  }
`;

const DateHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margn-top: 15px;
`;

const DateText = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: 700;
  font-size: 15px;
  text-align: center;
  flex: 1;
  color: ${({ theme }) => theme.colors.text};
`;

const ArrowButton = styled('button')`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  :hover {
    opacity: 0.7;
  }
  /* The disabled ink token sits close to body text in dark, so dim the whole
     control at the first and last day rather than rely on colour alone. */
  :disabled,
  :disabled:hover {
    opacity: 0.35;
    cursor: default;
  }
`;

// Anchors the click-to-open date-picker popover to the date label.
const DateAnchor = styled('div')`
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
`;

// Calendar popover dropped below the date label. z-index keeps it above the
// workout list; the panel header sits at the top of the panel so it isn't clipped.
// The react-datepicker overrides mirror the Copy Workout modal's TBDatePicker
// (shared/components/TBDatePicker), with the orange accent swapped for #444444.
const DatePickerPopover = styled('div')`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  padding: 8px;

  .react-datepicker {
    border: none;
    font-family: 'Nunito Sans';
    /* react-datepicker paints its own white surface and dark day text. */
    background: transparent;
    color: ${({ theme }) => theme.colors.text};
  }
  .react-datepicker__header {
    background: ${({ theme }) => theme.colors.surfaceElevated};
    border-bottom: none;
  }
  .react-datepicker__current-month,
  .react-datepicker__day-name,
  .react-datepicker__day {
    color: ${({ theme }) => theme.colors.text};
  }
  .react-datepicker__month-container {
    padding: 8px;
    font-family: 'Nunito Sans';
    font-size: 15px;
  }
  .react-datepicker__day-names, .react-datepicker__week {
    margin-bottom: 8px;
  }
  .react-datepicker__day--outside-month {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
  .react-datepicker__day--selected {
    background-color: ${({ theme }) => theme.colors.buttonPrimaryBg};
    color: ${({ theme }) => theme.colors.white};
  }
  .react-datepicker__day--keyboard-selected {
    background: none;
    color: ${({ theme }) => theme.colors.text};
  }
`;

// Pinned footer button — mirrors ExerciseDataBaseButton's styling (the "Create New
// Exercise" button), minus its absolute positioning so it pins via the flex layout.
const WorkoutEntryButton = styled('div')`
  background-color: ${({ theme }) => theme.colors.buttonPanelBg};
  width: 100%;
  display: flex;
  padding: 6px 16px;
  border-radius: 4px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  margin-top: 12px;
  transition: all 250ms ease-in-out;
  :hover {
    opacity: 0.7;
  }
`;

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const MessageContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  text-align: center;
`;

const ShimmerLine = styled('div')`
  display: flex;
  height: 40px;
  width: 100%;
  border-radius: 12px;
  margin-top: 16px;
`;

// Yellow warning box shown under the calendar athlete picker when the selected
// athlete's assignment carries a dateOffset. Mirrors WarningText in
// ConfirmDiscardPreSaveModal so the two read as the same "warning" affordance.
const OffsetWarning = styled('div')`
  display: flex;
  align-items: center;
  padding: 10px;
  margin-top: 12px;
  background: ${({ theme }) => theme.colors.warningSurface};
  border: 1px solid ${({ theme }) => theme.colors.warningBorder};
  border-radius: 4px;
  text-align: left;
  width: 100%;
  color: ${({ theme }) => theme.colors.warningText};
  p { color: inherit; }
`;

const PhoneSideBarContent = () => {
  const dispatch = useDispatch();

  // Click-to-open date picker for the date-based preview header.
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);
  const { colors } = useTheme();
  const customSelectStyles = useMemo(() => makeCustomSelectStyles(colors), [colors]);

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const calendarSelectAthletes = useSelector((state) => state.calendar.data.calendarSelectAthletes);
  const previewWorkoutItems = useSelector((state) => state.calendar.data.previewWorkoutItems);
  const isPreviewWorkoutsLoading = useSelector(
    (state) => state.calendar.data.isPreviewWorkoutsLoading,
  );
  const previewActiveExerciseIndex = useSelector(
    (state) => state.calendar.data.previewActiveExerciseIndex,
  );
  // Bumped (to moment()) after every committed calendar edit — our live-sync signal.
  const lastUpdateTime = useSelector((state) => state.calendar.data.lastUpdateTime);
  // The day that edit applied to ('YYYY-MM-DD') — used to skip refetch when previewing another day.
  const lastEditedDate = useSelector((state) => state.calendar.data.lastEditedDate);
  const previewWorkoutsError = useSelector((state) => state.calendar.data.previewWorkoutsError);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );

  const hasAthlete = Object.keys(selectedReduxAthlete).length > 0;
  const hasCalendar = !!selectedReduxCalendar?.id;
  const calendarId = selectedReduxCalendar?.id;
  // Program (free-form) mode: navigate by day number; the picker lists ALL athletes (we can't know
  // who has access up front) and the fetch fails gracefully into a "no access" message.
  const hasProgram = programType === 'free-form-program' && !!selectedReduxNonDateBasedProgram?.id;
  const dayBasedProgramId = selectedReduxNonDateBasedProgram?.id;
  const dayNumbers = useMemo(
    () => Object.keys(nonDateBasedProgramItems || {}).map(Number).sort((a, b) => a - b),
    [nonDateBasedProgramItems],
  );
  const programAthletes = useMemo(
    () => (calendarSelectAthletes || [])
      .filter((a) => !a.fake)
      .map((a) => ({ ...a, id: a.userId || a.id, name: `${a.first || ''} ${a.last || ''}`.trim() }))
      .sort((x, y) => x.name.localeCompare(y.name)),
    [calendarSelectAthletes],
  );
  const [programAthleteId, setProgramAthleteId] = useState(null);
  // Program preview day lives in redux (like previewDate) so day-header icons can target a day.
  // Program day numbers are 0-based (key 0 = Week 1 Day 1), so use ?? — `|| ` would skip day 0.
  // Driven solely by its own redux value so the preview stays independent of calendar navigation
  // (openers snapshot the selected day on open); falls back to the first day only transiently.
  const previewDayNumberRedux = useSelector((state) => state.calendar.data.previewDayNumber);
  const previewDayNumber = previewDayNumberRedux ?? dayNumbers[0] ?? 0;

  // Calendar mode: athletes assigned to the selected calendar, alphabetical, as dropdown options.
  // Each athlete's calendar id lives in its `calendarAssignment` object.
  const calendarAthletes = useMemo(() => {
    const cid = String(calendarId);
    return (calendarSelectAthletes || [])
      .filter((a) => !a.fake && (
        String(a.calendarAssignment?.calendarId) === cid
        || String(a.calendarAssignment?.id) === cid
      ))
      .map((a) => ({ ...a, name: `${a.first || ''} ${a.last || ''}`.trim() }))
      .sort((x, y) => x.name.localeCompare(y.name));
  }, [calendarSelectAthletes, calendarId]);

  // Which athlete the calendar-mode dropdown is showing (defaults to the first below).
  const [calendarAthleteId, setCalendarAthleteId] = useState(null);

  // Assigning the selected program to the picked athlete (from the "no access" state).
  const [isAssigning, setIsAssigning] = useState(false);

  // Stable primitives — keying effects on these (not the moment/object references) prevents
  // duplicate fetches when redux returns a new reference with the same value.
  let athleteId = null;
  if (hasAthlete) {
    athleteId = selectedReduxAthlete?.id;
  } else if (hasCalendar) {
    athleteId = calendarAthleteId;
  }
  // Preview date (YYYY-MM-DD string) lives in redux so openers can target a day: the phone tab
  // sets today, a day-header preview icon sets that day. Independent of the calendar selectedDate.
  const previewDateRedux = useSelector((state) => state.calendar.data.previewDate);
  const previewDate = previewDateRedux || moment().format('YYYY-MM-DD');

  // Fetch the athlete's workouts once per (athlete, preview date) change.
  useEffect(() => {
    if (athleteId && Object.keys(currentUser).length) {
      dispatch(setPreviewActiveExerciseIndex(null));
      dispatch(getPreviewWorkoutItems(currentUser, athleteId, previewDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId, previewDate]);

  // Refetch the preview when a calendar edit is committed (reps/sets/supersets/add/delete/
  // swap/reorder all bump lastUpdateTime) — but ONLY when the edit was on the day we're previewing.
  // Editing another day is left for navigation to pick up, so the preview isn't disrupted.
  useEffect(() => {
    if (!lastUpdateTime || hasProgram) return; // null on mount; program mode handled below
    if (previewWorkoutsError) return; // preview isn't showing (error/no-access) — nothing to sync
    const editedDates = Array.isArray(lastEditedDate) ? lastEditedDate : [lastEditedDate];
    if (!editedDates.includes(previewDate)) return; // edit didn't touch the previewed day
    if (athleteId && Object.keys(currentUser).length) {
      dispatch(getPreviewWorkoutItems(currentUser, athleteId, previewDate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdateTime]);

  // Program mode live-sync: free-form edits put the edited day NUMBER (0-based) in lastEditedDate.
  // Refetch only when that day is on screen, so editing another day doesn't disrupt the preview;
  // navigation picks up the rest. previewDayNumber and the signalled day are both 0-based template
  // day keys, so they compare directly.
  useEffect(() => {
    if (!lastUpdateTime || !hasProgram) return;
    if (previewWorkoutsError) return; // preview isn't showing (error/no-access) — nothing to sync
    const editedDays = Array.isArray(lastEditedDate) ? lastEditedDate : [lastEditedDate];
    if (!editedDays.map(Number).includes(Number(previewDayNumber))) return;
    if (dayBasedProgramId && programAthleteId && Object.keys(currentUser).length) {
      dispatch(getPreviewWorkoutItemsFreeForm(
        currentUser,
        programAthleteId,
        selectedReduxNonDateBasedProgram,
        previewDayNumber,
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUpdateTime]);

  // Calendar mode: default the dropdown to the first athlete (and recover if the current
  // selection drops out of the list) so a preview always shows. If the calendar has NO assigned
  // athletes, clear the selection so the stale preview from the previous calendar is dropped.
  useEffect(() => {
    if (!hasCalendar) return;
    if (!calendarAthletes.length) {
      if (calendarAthleteId !== null) setCalendarAthleteId(null);
      return;
    }
    const stillThere = calendarAthletes.some((a) => a.id === calendarAthleteId);
    if (!stillThere) setCalendarAthleteId(calendarAthletes[0].id);
  }, [hasCalendar, calendarAthletes, calendarAthleteId]);

  // Program mode: default the picker to the first athlete (recover if it drops out of the list).
  useEffect(() => {
    if (!hasProgram || !programAthletes.length) return;
    const stillThere = programAthletes.some((a) => a.id === programAthleteId);
    if (!stillThere) setProgramAthleteId(programAthletes[0].id);
  }, [hasProgram, programAthletes, programAthleteId]);

  // Program mode: fetch the selected athlete's view of the program day (resets on athlete/day).
  useEffect(() => {
    const hasUser = Object.keys(currentUser).length;
    if (hasProgram && dayBasedProgramId && programAthleteId && hasUser) {
      dispatch(setPreviewActiveExerciseIndex(null));
      // Pass the program object — the thunk resolves the athlete's dayBasedProgramId from it.
      dispatch(getPreviewWorkoutItemsFreeForm(
        currentUser,
        programAthleteId,
        selectedReduxNonDateBasedProgram,
        previewDayNumber,
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProgram, dayBasedProgramId, programAthleteId, previewDayNumber]);

  // Close the date picker on any click outside the date label + popover.
  useEffect(() => {
    if (!showDatePicker) return undefined;
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const { flat, rendered } = useMemo(
    () => flattenPreviewWorkouts(previewWorkoutItems),
    [previewWorkoutItems],
  );

  // Blank panel unless viewing a single athlete, a calendar, or a free-form program.
  if (!hasAthlete && !hasCalendar && !hasProgram) {
    return null;
  }

  const stepDay = (amount) => {
    dispatch(setPreviewDate(moment(previewDate).add(amount, 'day').format('YYYY-MM-DD')));
  };

  // Program mode: step to the prev/next real program day (handles non-contiguous day numbers).
  const dayIndex = dayNumbers.indexOf(previewDayNumber);
  const isFirstDay = dayIndex <= 0;
  const isLastDay = dayIndex === -1 || dayIndex >= dayNumbers.length - 1;
  const stepDayNumber = (amount) => {
    const nextIndex = (dayIndex === -1 ? 0 : dayIndex) + amount;
    if (nextIndex >= 0 && nextIndex < dayNumbers.length) {
      dispatch(setPreviewDayNumber(dayNumbers[nextIndex]));
    }
  };
  // Program header label: "Week W Day D" (matches the calendar's own week/day numbering),
  // falling back to the raw day number if the week/day breakdown isn't available.
  const programDay = nonDateBasedProgramItems?.[previewDayNumber];
  const programDayLabel = programDay?.weekNumber && programDay?.dayInWeek
    ? `Week ${programDay.weekNumber} Day ${programDay.dayInWeek}`
    : `Day ${previewDayNumber}`;

  // Assign the selected program to the picked athlete, then refetch the preview for that SAME
  // athlete so it shows immediately (the picker keeps its selection — programAthleteId untouched).
  const handleAssignProgram = () => {
    if (!programAthleteId || !selectedReduxNonDateBasedProgram?.id) return;
    setIsAssigning(true);
    const saveInfoIds = [selectedReduxNonDateBasedProgram.id];
    dispatch(assignProgramToAthlete(currentUser, programAthleteId, saveInfoIds))
      .then((res) => {
        showSuccessToast('Program assigned');
        // Use the assignment response's program list so the refetch skips the 403ing list GET.
        dispatch(getPreviewWorkoutItemsFreeForm(
          currentUser,
          programAthleteId,
          selectedReduxNonDateBasedProgram,
          previewDayNumber,
          res?.data?.programs,
        ));
      })
      .catch((err) => showErrorToast(err?.response?.headers?.message || 'Failed to assign program'))
      .finally(() => setIsAssigning(false));
  };

  const isDetailView = previewActiveExerciseIndex !== null
    && !!flat[previewActiveExerciseIndex];

  // Name of the athlete currently being previewed (shown as the detail title in calendar/program).
  const calendarAthleteName = calendarAthletes.find((a) => a.id === calendarAthleteId)?.name;
  // Assignment (Start Date / Offset) for the picked calendar athlete — calendar view only.
  const selectedCalendarAssignment = calendarAthletes
    .find((a) => a.id === calendarAthleteId)?.calendarAssignment;
  const programAthleteName = programAthletes.find((a) => a.id === programAthleteId)?.name;
  const detailAthleteName = hasProgram ? programAthleteName : calendarAthleteName;

  // Viewing a calendar that has no assigned athletes — nothing to preview.
  const noCalendarAthletes = hasCalendar && !calendarAthletes.length;
  // Program mode: the selected athlete isn't assigned this program (the fetch was rejected).
  const programNoAccess = hasProgram && !isPreviewWorkoutsLoading && !!previewWorkoutsError;

  const renderBody = () => {
    if (noCalendarAthletes) {
      return (
        <MessageContainer>
          <Text fontWeight={700} fontSize='14px'>No athlete is assigned to this calendar, choose an athlete calendar to preview their workout OR assign athletes to this calendar via Manage Users.</Text>
        </MessageContainer>
      );
    }
    if (programNoAccess) {
      return (
        <MessageContainer>
          <div>
            <Text fontWeight={700} fontSize='14px'>This athlete does not have access to this program. To manage access, go to Manage Users or click the button below to assign this program to the athlete.</Text>
            <div style={{
              marginTop: '16px', display: 'flex', justifyContent: 'center',
            }}
            >
              <div style={{ width: '185px' }}>
                <Button
                  cta='Assign Program'
                  primary
                  large
                  noBorder
                  fullWidth
                  upperCase={false}
                  fontSize={16}
                  fontWeight={700}
                  letterSpacing={0}
                  customBorderRadius='8px'
                  customColor={colors.buttonPrimaryBg}
                  customTextColor='white'
                  onClick={handleAssignProgram}
                  disabled={isAssigning || !programAthleteId}
                  isLoading={isAssigning}
                />
              </div>
            </div>
          </div>
        </MessageContainer>
      );
    }
    if (isPreviewWorkoutsLoading) {
      return (
        <Shimmer>
          <ShimmerLine />
          <ShimmerLine />
          <ShimmerLine />
          <ShimmerLine />
        </Shimmer>
      );
    }
    if (isDetailView) {
      return (
        <>
          {hasCalendar || hasProgram ? <SideBarTitleWrapper title={detailAthleteName} /> : null}
          <div style={{ flex: 1, overflow: 'auto' }}>
            <ZoomWrapper>
              <CalendarExerciseDetail workoutObject={flat[previewActiveExerciseIndex]} />
            </ZoomWrapper>
          </div>
          <CalendarExerciseNav
            activeIndex={previewActiveExerciseIndex}
            total={flat.length}
            onPrev={() => dispatch(setPreviewActiveExerciseIndex(previewActiveExerciseIndex - 1))}
            onNext={() => dispatch(setPreviewActiveExerciseIndex(previewActiveExerciseIndex + 1))}
            onClose={() => dispatch(setPreviewActiveExerciseIndex(null))}
          />
        </>
      );
    }
    if (rendered.length) {
      // Workout Entry navigation target. Free-form programs open the Workout Entry landing page
      // with the previewed athlete preselected (the user picks the program there); date-based
      // calendars deep-link to the athlete's day.
      let workoutEntryHref = null;
      if (hasProgram) {
        workoutEntryHref = programAthleteId ? `/workouts?athleteId=${programAthleteId}` : '/workouts';
      } else if (athleteId) {
        workoutEntryHref = `/workouts?date=${previewDate}&athleteId=${athleteId}`;
      }
      return (
        <>
          <CalendarWorkoutPreviewList
            renderedItems={rendered}
            onSelectExercise={(flatIndex) => dispatch(setPreviewActiveExerciseIndex(flatIndex))}
          />
          {workoutEntryHref ? (
            <WorkoutEntryButton onClick={() => window.open(workoutEntryHref, '_blank', 'noopener,noreferrer')}>
              <Text color='white' fontWeight={700} fontSize='16px'>
                {hasProgram ? 'Go To Workout Entry' : 'View In Workout Entry'}
              </Text>
              <IcomoonReact iconSet={iconSet} size={12} icon='alt-right-arrow' color='white' />
            </WorkoutEntryButton>
          ) : null}
        </>
      );
    }
    return (
      <MessageContainer>
        <Text fontWeight={700} fontSize='14px'>No Workout for the Selected Day</Text>
      </MessageContainer>
    );
  };

  return (
    <Wrapper>
      {!isDetailView && hasProgram ? (
        <DateHeader>
          <ArrowButton type='button' disabled={isFirstDay} onClick={() => stepDayNumber(-1)} aria-label='Previous day'>
            <IcomoonReact iconSet={iconSet} size={18} icon='left-arrow' color={isFirstDay ? colors.textDisabled : colors.text} />
          </ArrowButton>
          <DateText>{programDayLabel}</DateText>
          <ArrowButton type='button' disabled={isLastDay} onClick={() => stepDayNumber(1)} aria-label='Next day'>
            <IcomoonReact iconSet={iconSet} size={18} icon='right-arrow' color={isLastDay ? colors.textDisabled : colors.text} />
          </ArrowButton>
        </DateHeader>
      ) : null}
      {!isDetailView && !hasProgram && !noCalendarAthletes ? (
        <DateHeader>
          <ArrowButton type='button' onClick={() => stepDay(-1)} aria-label='Previous day'>
            <IcomoonReact iconSet={iconSet} size={18} icon='left-arrow' color={colors.text} />
          </ArrowButton>
          <DateAnchor ref={datePickerRef}>
            <DateText
              role='button'
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => setShowDatePicker((v) => !v)}
            >
              {moment(previewDate).format('MMMM D, YYYY')}
            </DateText>
            {showDatePicker ? (
              <DatePickerPopover>
                <DatePicker
                  inline
                  selected={moment(previewDate, 'YYYY-MM-DD').toDate()}
                  onChange={(d) => {
                    dispatch(setPreviewDate(moment(d).format('YYYY-MM-DD')));
                    setShowDatePicker(false);
                  }}
                  renderCustomHeader={({
                    decreaseMonth,
                    increaseMonth,
                    date,
                  }) => (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '12px',
                    }}
                    >
                      <button
                        type='button'
                        onClick={decreaseMonth}
                        style={{
                          border: 'none',
                          marginLeft: '10px',
                          background: 'rgba(95, 96, 100, 0.05)',
                          borderRadius: '5px',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IcomoonReact iconSet={iconSet} size={15} icon='left-arrow' color={colors.text} />
                      </button>
                      <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: 'Nunito Sans',
                        fontWeight: '700',
                        fontSize: '16px',
                      }}
                      >
                        {date?.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button
                        type='button'
                        onClick={increaseMonth}
                        style={{
                          border: 'none',
                          marginRight: '10px',
                          background: 'rgba(95, 96, 100, 0.05)',
                          borderRadius: '5px',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IcomoonReact iconSet={iconSet} size={15} icon='right-arrow' color={colors.text} />
                      </button>
                    </div>
                  )}
                />
              </DatePickerPopover>
            ) : null}
          </DateAnchor>
          <ArrowButton type='button' onClick={() => stepDay(1)} aria-label='Next day'>
            <IcomoonReact iconSet={iconSet} size={18} icon='right-arrow' color={colors.text} />
          </ArrowButton>
        </DateHeader>
      ) : null}
      {hasCalendar && !isDetailView && calendarAthletes.length ? (
        <SelectBox
          titleStyledProps={{ fontSize: '13px', fontWeight: 700 }}
          options={calendarAthletes}
          value={calendarAthletes.find((a) => a.id === calendarAthleteId) || null}
          onChangeFunction={(opt) => setCalendarAthleteId(opt ? opt.id : null)}
          isClearable={false}
          placeholder='Select athlete'
          customStyles={customSelectStyles}
          menuListComponent={MemoizedMenuList}
          styledProps={{ height: '', paddingTop: 20 }}
          styledPropsInput={{ width: '100% !important' }}
        />
      ) : null}
      {hasCalendar && !isDetailView && calendarAthletes.length
        && selectedCalendarAssignment?.dateOffset ? (
          <OffsetWarning>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='warning'
              color='#856404'
              style={{ marginRight: '8px', flexShrink: 0 }}
            />
            <Text fontSize='15px' color='#856404'>Athlete has an offset calendar</Text>
          </OffsetWarning>
        ) : null}
      {hasProgram && !isDetailView ? (
        <SelectBox
          titleStyledProps={{ fontSize: '13px', fontWeight: 700 }}
          options={programAthletes}
          value={programAthletes.find((a) => a.id === programAthleteId) || null}
          onChangeFunction={(opt) => setProgramAthleteId(opt ? opt.id : null)}
          isClearable={false}
          placeholder='Select athlete'
          customStyles={customSelectStyles}
          menuListComponent={MemoizedMenuList}
          styledProps={{ height: '', paddingTop: 20 }}
          styledPropsInput={{ width: '100% !important' }}
        />
      ) : null}

      <Body>{renderBody()}</Body>
    </Wrapper>
  );
};

export default PhoneSideBarContent;
